import { togglePendingStoreActions } from "@/utils/decorators";
import { API } from "@/config";

import {
  SET_TOKEN_VALIDATION_STATE,
  FILL_AUTH_SERVICE_POINT_DATA,
  TOGGLE_WAITING_FOR_AUTH_SERVICE_POINT_DATA,
  SET_STATE_FOR_WAITING_FOR_AUTH_SERVICE_POINT_DATA,
  SET_AFTER_AUTHORIZATION_URL,
  SET_FORM_TYPE,
  SET_LOGIN_TOKEN,
  SET_QR_CODE,
  SET_FORM_PARAMS,
  SET_OTP_STAMP,
  SET_BLOCK_TIME
} from "./mutations-types";

export default {
  async getAuthServicePointSettingsByToken({ commit, dispatch }, token = "") {
    try {
      const {
        params,
        type,
        platform_url,
        gateway,
        lang,
        brand,
        support_email
      } = (await API.get(
        `/authServicesPoints/getAuthServicePointSettingsByToken?token=${token}`
      )).data.data;
      commit(FILL_AUTH_SERVICE_POINT_DATA, {
        params,
        type,
        platform_url,
        gateway
      });
      commit(SET_TOKEN_VALIDATION_STATE, { isValid: true });
      commit(TOGGLE_WAITING_FOR_AUTH_SERVICE_POINT_DATA);

      dispatch(
        "global/setPropertiesOfThePlatform",
        { lang, brand, support_email },
        { root: true }
      );
    } catch (err) {
      commit(SET_TOKEN_VALIDATION_STATE, { isValid: false });
      commit(TOGGLE_WAITING_FOR_AUTH_SERVICE_POINT_DATA);
    }

    return token;
  },

  @togglePendingStoreActions()
  async changeFormState({ commit }, state) {
    try {
      commit(SET_FORM_TYPE, state);
    } catch (err) {
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async loginToASP({ commit }, { params, token, gateway }) {
    try {
      const response = (await API.post("/authServicesPoints/loginToASP", {
        params,
        token,
        gateway
      })).data.data;
      // handle basic auth (login, password)
      if (response.type === "session") {
        const url = response.url;
        commit(SET_AFTER_AUTHORIZATION_URL, { url });
        return;
      }
      if (response.type === "access") {
        commit(SET_LOGIN_TOKEN, response.token);
        commit(SET_FORM_TYPE, "two_factor_login");
        commit(SET_OTP_STAMP, response.otpStamp);
        commit(SET_FORM_PARAMS, ["otpTimer", "twoFactorDigits"]);
        return;
      }
      if (response.type === "setup") {
        const { secret, token: access_token, otpStamp } = (await API.post(
          "/authServicesPoints/create2fa",
          { token: response.token }
        )).data.data;
        commit(SET_QR_CODE, secret);
        commit(SET_LOGIN_TOKEN, access_token);
        commit(SET_FORM_TYPE, "two_factor_setup");
        commit(SET_OTP_STAMP, otpStamp);
        commit(SET_FORM_PARAMS, ["otpTimer", "qrCode"]);
        return;
      }

      if (response.type === "password_change") {
        commit(SET_LOGIN_TOKEN, response.token);
        commit(SET_FORM_TYPE, "two_factor_password_change");
        commit(SET_FORM_PARAMS, ["new_password", "change_password"]);
        return;
      }
    } catch (err) {
      const data = err.response.data.data;
      if (data.user === "blocked") {
        commit(SET_BLOCK_TIME, data.unblocks_at);
        commit(SET_FORM_TYPE, "blocked_user");
        commit(SET_FORM_PARAMS, ["blockedUser"]);
      }
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async registerToASP({ commit }, { params, token, gateway }) {
    try {
      await API.post("/authServicesPoints/registerToASP", {
        params,
        token,
        gateway
      });
      commit(SET_FORM_TYPE, "confirm");
    } catch (err) {
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async changePasswordToASP({ commit }, { params, token, gateway }) {
    try {
      params.platform = window.location.origin;
      await API.post("/authServicesPoints/changePasswordToASP", {
        params,
        token,
        gateway
      });
      commit(SET_FORM_TYPE, "email_change_password");
    } catch (err) {
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async confirmChangePasswordToASP({ commit }, { params, token, gateway }) {
    try {
      await API.post("/authServicesPoints/confirmChangePasswordToASP", {
        params,
        token,
        gateway
      });
      commit(SET_FORM_TYPE, "confirm_new_change_password");
    } catch (err) {
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async sendVerificationCode({ commit }, { params, token, gateway }) {
    try {
      await API.post("/authServicesPoints/sendVerificationCode", {
        params,
        token,
        gateway
      });
      commit(SET_FORM_TYPE, "verification");
    } catch (err) {
      console.log(err);
    }
  },

  @togglePendingStoreActions()
  async resendVerificationCode(_, { params, token, gateway }) {
    try {
      await API.post("/authServicesPoints/sendVerificationCode", {
        params,
        token,
        gateway
      });
      // commit(SET_FORM_TYPE, 'verification');
    } catch (err) {
      console.log(err);
    }
  },

  setStateForWaitingForAuthServicePointData({ commit }, newState) {
    commit(SET_STATE_FOR_WAITING_FOR_AUTH_SERVICE_POINT_DATA, newState);
  },
  twoFactorSetup({ commit }, { params, token, gateway }) {
    commit(SET_FORM_TYPE, "two_factor_login");
    commit(SET_FORM_PARAMS, ["otpTimer", "twoFactorDigits"]);
  },
  @togglePendingStoreActions()
  async twoFactorLogin({ commit, state }, { params, token, gateway }) {
    try {
      if (!params.code || params.code < 6) return;
      const { url } = (await API.post("/authServicesPoints/verify2fa", {
        code: params.code,
        token: state.loginToken
      })).data.data;
      commit(SET_AFTER_AUTHORIZATION_URL, { url });
      return;
    } catch (err) {
      console.log(err);
    }
  },
  @togglePendingStoreActions()
  async twoFactorPasswordChange({ commit, state }, { params, token, gateway }) {
    try {
      const response = (await API.post(
        "/authServicesPoints/changePassword2fa",
        { token: state.loginToken, password: params.change_password }
      )).data.data;

      if (response.type === "session") {
        const url = response.url;
        commit(SET_AFTER_AUTHORIZATION_URL, { url });
        return;
      }

      const { secret, token: access_token, otpStamp } = (await API.post(
        "/authServicesPoints/create2fa",
        { token: response.token }
      )).data.data;
      commit(SET_QR_CODE, secret);
      commit(SET_LOGIN_TOKEN, access_token);
      commit(SET_FORM_TYPE, "two_factor_setup");
      commit(SET_OTP_STAMP, otpStamp);
      commit(SET_FORM_PARAMS, ["otpTimer", "qrCode"]);
    } catch (err) {
      console.log(err);
    }
  },
  resetParams({ state, commit }) {
    commit(SET_FORM_PARAMS, state.initialParams);
  },
  goBackToLogin({ state, commit }) {
    commit(SET_FORM_TYPE, "login");
    commit(SET_FORM_PARAMS, state.initialParams);
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/authServicesPoints/actions.js